import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const SERVER_URL = process.env.NODE_ENV === "development" ? "http://localhost:5002" : "https://panel.youvati.com";

const fmtDate = (unix) => {
    if (!unix) return "-";
    return new Date(unix * 1000).toLocaleDateString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
    });
};

const STATUS_META = {
    active:    { bg: "#dcfce7", color: "#15803d", label: "Active" },
    cancelled: { bg: "#fee2e2", color: "#dc2626", label: "Cancelled" },
    paused:    { bg: "#fef9c3", color: "#a16207", label: "Paused" },
    completed: { bg: "#e0f2fe", color: "#0369a1", label: "Completed" },
    created:   { bg: "#f3e8ff", color: "#7c3aed", label: "Created" },
    halted:    { bg: "#fce7f3", color: "#be185d", label: "Halted" },
    expired:   { bg: "#f4f4f5", color: "#71717a", label: "Expired" },
};

const StatusBadge = ({ status }) => {
    const m = STATUS_META[status] || { bg: "#f4f4f5", color: "#71717a", label: status };
    return (
        <span style={{
            background: m.bg, color: m.color,
            padding: "3px 10px", borderRadius: "20px",
            fontSize: "12px", fontWeight: 700,
        }}>
            {m.label}
        </span>
    );
};

const TABS = [
    { key: "active",    label: "Active" },
    { key: "cancelled", label: "Cancelled" },
    { key: "paused",    label: "Paused" },
];

export default function RazorpaySubscriptions() {
    const navigate = useNavigate();

    useEffect(() => {
        const email = localStorage.getItem("email");
        const password = localStorage.getItem("password");
        const remember = localStorage.getItem("remember");
        if (remember === "false" || !email || !password) navigate("/signin");
    }, [navigate]);

    const [allSubs, setAllSubs]       = useState([]);
    const [loading, setLoading]       = useState(false);
    const [error, setError]           = useState("");
    const [activeTab, setActiveTab]   = useState("active");
    const [search, setSearch]         = useState("");
    const [fetchCount, setFetchCount] = useState(100);

    const fetchSubs = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await fetch(
                `${SERVER_URL}/admin/subscriptions?count=${fetchCount}`,
                { headers: { "x-admin-token": "youvati_admin_2024" } }
            );
            if (!res.ok) throw new Error(`Server error: ${res.status}`);
            const data = await res.json();
            setAllSubs((data.items || []).filter(s => s.status !== "created"));
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [fetchCount]);

    useEffect(() => { fetchSubs(); }, [fetchSubs]);

    const byTab = (tab) => tab ? allSubs.filter(s => s.status === tab) : allSubs;

    const filtered = byTab(activeTab).filter(s => {
        if (!search.trim()) return true;
        const q = search.trim().toLowerCase();
        return (
            (s.id || "").toLowerCase().includes(q) ||
            (s.plan_id || "").toLowerCase().includes(q) ||
            (s.customer_id || "").toLowerCase().includes(q) ||
            Object.values(s.notes || {}).some(v => String(v).toLowerCase().includes(q))
        );
    });

    const handleExport = () => {
        const rows = filtered.map((s, i) => ({
            "#": i + 1,
            "Subscription ID": s.id || "",
            "Plan ID": s.plan_id || "",
            "Email": s.customer?.email || s.notes?.email || "",
            "Phone": s.customer?.contact || s.notes?.phone || "",
            "Status": s.status || "",
            "Paid": s.paid_count ?? "",
            "Total": s.total_count ?? "",
            "Current Start": fmtDate(s.current_start),
            "Current End": fmtDate(s.current_end),
            "Next Charge": fmtDate(s.charge_at),
            "Created": fmtDate(s.created_at),
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Subscriptions");
        saveAs(
            new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array" })],
                { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
            `razorpay_subscriptions_${new Date().toISOString().slice(0, 10)}.xlsx`
        );
    };

    return (
        <div className="container-fluid py-4">

            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h3 className="mb-0">Razorpay Subscriptions</h3>
                <Link to="/" className="btn btn-outline-secondary btn-sm">Back</Link>
            </div>

            {/* CONTROLS */}
            <div className="card mb-3">
                <div className="card-body py-3 px-4">
                    <div className="d-flex gap-3 align-items-center flex-wrap">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by ID, plan, customer…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{ maxWidth: "280px" }}
                        />
                        <div className="d-flex align-items-center gap-2">
                            <label className="mb-0 text-muted" style={{ fontSize: "13px" }}>Fetch:</label>
                            <select
                                className="form-select form-select-sm"
                                value={fetchCount}
                                onChange={e => setFetchCount(Number(e.target.value))}
                                style={{ width: "90px" }}
                            >
                                {[100, 200, 500, 1000].map(n => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={fetchSubs}
                            disabled={loading}
                        >
                            {loading ? "Loading…" : "Refresh"}
                        </button>
                        <button
                            className="btn btn-success btn-sm ms-auto"
                            onClick={handleExport}
                            disabled={loading || filtered.length === 0}
                        >
                            Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* TABS */}
            <ul className="nav nav-tabs mb-3">
                {TABS.map(tab => (
                    <li className="nav-item" key={tab.key}>
                        <button
                            className={`nav-link ${activeTab === tab.key ? "active fw-bold" : ""}`}
                            onClick={() => setActiveTab(tab.key)}
                            style={activeTab !== tab.key ? { color: "#fff" } : {}}
                        >
                            {tab.label}
                            <span className="badge bg-secondary ms-2" style={{ fontSize: "11px" }}>
                                {byTab(tab.key).length}
                            </span>
                        </button>
                    </li>
                ))}
            </ul>

            {/* ERROR */}
            {error && <div className="alert alert-danger">{error}</div>}

            {/* TABLE */}
            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" />
                    <div className="mt-2 text-muted">Loading subscriptions…</div>
                </div>
            ) : (
                <div className="card">
                    <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
                            <thead className="table-dark">
                                <tr>
                                    <th>#</th>
                                    <th>Subscription ID</th>
                                    <th>Plan ID</th>
                                    <th>Customer</th>
                                    <th className="text-center">Status</th>
                                    <th>Next Charge</th>
                                    <th>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="text-center py-4 text-muted">
                                            No subscriptions found
                                        </td>
                                    </tr>
                                ) : filtered.map((s, i) => (
                                    <tr key={s.id}>
                                        <td>{i + 1}</td>
                                        <td style={{ fontFamily: "monospace", fontSize: "11px" }}>{s.id}</td>
                                        <td style={{ fontFamily: "monospace", fontSize: "11px" }}>{s.plan_id || "-"}</td>
                                        <td>
                                            <div style={{ fontSize: "13px" }}>{s.customer?.email || s.notes?.email || "-"}</div>
                                            <div style={{ fontSize: "12px", color: "#6b7280" }}>{s.customer?.contact || s.notes?.phone || "-"}</div>
                                        </td>
                                        <td className="text-center">
                                            <StatusBadge status={s.status} />
                                        </td>
                                        <td style={{ whiteSpace: "nowrap" }}>{fmtDate(s.charge_at)}</td>
                                        <td style={{ whiteSpace: "nowrap" }}>{fmtDate(s.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="card-footer text-muted" style={{ fontSize: "12px" }}>
                        {filtered.length} of {allSubs.length} subscriptions shown
                    </div>
                </div>
            )}
        </div>
    );
}
